<template>
  <section
    class="search-inline-gallery-section"
    :class="{
      'gallery-in-use': galleryInUse,
      'search-inline-gallery-loading-background':
        firstImageLoaded || currentImage > 1,
    }"
  >
    <translate
      v-show="firstImageLoaded || currentImage > 1"
      tag="p"
      class="search-inline-gallery-loading-message"
    >
      Loading image...
    </translate>

    <div
      :id="`search-gallery-${searchResultIndex}`"
      ref="gallery"
      class="search-inline-gallery"
      :class="{ desktop: $isDesktop }"
    >
      <img
        v-for="(photo, index) in galleryPhotos"
        :key="'photo-' + index"
        :ref="`imageElements`"
        :data-caption="capitalize(photo.caption)"
        :alt="capitalize(photoCaptionAltFormatter(photo.caption))"
        v-bind="imgProps(photo)"
        class="search-inline-gallery-element"
        sizes="100vw"
        width="428"
        height="321"
        :loading="index === 0 && searchResultIndex === 0 ? 'eager' : 'lazy'"
        :fetchpriority="index === 0 && searchResultIndex === 0 ? 'high' : 'low'"
        :decoding="index === 0 && searchResultIndex === 0 ? 'sync' : 'async'"
      />
    </div>

    <!-- image count -->
    <div v-if="photos.length > 1 && photoCount !== 0" class="gallery-footer">
      <div class="photo-count-wrapper">
        <i class="photo-count"
          >{{ currentImage }}/{{ photoCount ? photoCount : photos.length }}</i
        >
      </div>
      <div class="gallery-scrollbar">
        <span
          :style="{ width: (100 / photoCount) * currentImage + '%' }"
          class="scrollbar-pip"
        />
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import type { PitchupPhoto } from '~/common/photos/types'
import { photoCaptionAltFormatter } from '~/utility/photoCaptionAltFormatter'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import { capitalize } from '~/utility/text'
import { transformImageSrcSet } from '../../../utility/image-url'

@Component
class SearchInlineGallery extends Vue {
  @Prop({ required: true })
  primaryPhoto: PitchupPhoto

  @Prop({ required: true })
  photos: PitchupPhoto[]

  @Prop({ required: false })
  photoCount: number

  @Prop()
  searchResultIndex: number

  imgProps(photo) {
    const props = {
      srcset: transformImageSrcSet(`
        /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=360,h=270${photo.url.masterImage} 360w,
        /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=30,w=720,h=540${photo.url.masterImage} 840w,
        /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=30,w=1080,h=810${photo.url.masterImage} 1080w
      `),
      src: `/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=360,h=270${photo.url.masterImage}`,
    }

    return props
  }

  get galleryPhotosLength() {
    // Round up to nearest 5 from current image
    // then add 5 so we always have at least +5
    // images loaded from current image in view
    return Math.ceil(this.currentImage / 5) * 5 + 5
  }

  get galleryPhotos() {
    // ensure primary is first
    const photos: PitchupPhoto[] = []
    if (this.primaryPhoto) {
      photos.push(this.primaryPhoto)
    }
    ;(this.photos || [])
      .slice(0, this.galleryPhotosLength)
      .filter((photo) => photo?.url?.masterImage)
      .forEach((photo) => {
        if (
          this.isDifferentPhoto(
            photo.url.masterImage,
            photos[0].url.masterImage,
          )
        ) {
          photos.push(photo)
        }
      })

    return photos
  }

  isDifferentPhoto(firstUrl: string, secondUrl: string) {
    const firstPhoto = firstUrl.split('/')
    const secondPhoto = secondUrl.split('/')
    return (
      firstPhoto[firstPhoto.length - 1] !== secondPhoto[secondPhoto.length - 1]
    )
  }

  currentImage = 1
  galleryInUse = false
  firstImageLoaded = false

  async mounted() {
    await this.$nextTick(() => {
      const gallery = this.$refs.gallery as HTMLDivElement
      if (gallery) {
        const scrollPos = gallery?.dataset?.galleryPosition
        if (scrollPos) {
          gallery.scrollLeft = parseInt(scrollPos)
          this.debounce(this.calculateGalleryPosition)
        } else {
          gallery.scrollLeft = 0
          this.debounce(this.calculateGalleryPosition)
        }
      }
      gallery?.addEventListener('scroll', () => {
        this.galleryInUse = true
        this.debounce(this.calculateGalleryPosition)
      })
    })
    this.checkFirstImageLoaded()
  }

  @Watch('galleryPhotos', { immediate: true })
  onGalleryPhotosChanged() {
    this.checkFirstImageLoaded()
  }

  checkFirstImageLoaded() {
    if (process.server || this.firstImageLoaded) {
      return
    }
    if (this.galleryPhotos.length > 0) {
      const img = new Image()
      const { src, srcset } = this.imgProps(this.galleryPhotos[0])
      img.src = src
      img.srcset = srcset

      img.onload = () => {
        setTimeout(() => {
          this.firstImageLoaded = true
        }, 500)
      }
    }
  }

  calculateGalleryPosition() {
    const gallery = this.$refs.gallery as HTMLDivElement
    if (!gallery) {
      return
    }

    const galleryScrollLeft = gallery.scrollLeft
    const galleryImages = gallery.children

    if (galleryScrollLeft === 0) {
      this.currentImage = 1
    }

    if (galleryScrollLeft > 0 && galleryImages) {
      for (let i = 0; i < galleryImages.length; i++) {
        if (
          galleryScrollLeft + 50 >
            (galleryImages[i] as HTMLImageElement).offsetLeft &&
          galleryScrollLeft - 50 <
            (galleryImages[i] as HTMLImageElement).offsetLeft
        ) {
          this.currentImage = i + 1
          break
        }
      }
    }
    this.galleryInUse = false
  }

  debouncedFunction: ReturnType<typeof setTimeout>
  debounce(functionToDebounce) {
    clearTimeout(this.debouncedFunction)
    this.debouncedFunction = setTimeout(functionToDebounce, 500)
  }

  capitalize(value: string) {
    return capitalize(value)
  }

  photoCaptionAltFormatter(caption) {
    return photoCaptionAltFormatter(caption)
  }

  activated() {
    const gallery = this.$refs.gallery as HTMLDivElement
    if (!gallery) {
      return
    }

    if (this.currentImage !== 1) {
      gallery.scrollLeft = (
        gallery.children[this.currentImage - 1] as HTMLImageElement
      ).offsetLeft
    } else {
      gallery.scrollLeft = 0
    }
  }
}

export default toNative(SearchInlineGallery)
</script>

<style scoped>
.search-inline-gallery-section {
  position: relative;
}

.search-inline-gallery-loading-background {
  background: #bec1c6 url('~/assets/pitchup.com-vector-logo.svg') no-repeat
    center center;
  background-size: 35% 35%;
  background-position-y: 35%;
}

.search-inline-gallery {
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  position: relative;
  scrollbar-width: none;
}

.search-inline-gallery-element {
  flex-shrink: 0;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  width: 100%;
  max-height: 69vw;
}

.search-inline-gallery-loading-message {
  bottom: 32%;
  color: var(--colour-text-light);
  font-size: var(--small);
  font-style: italic;
  left: auto;
  position: absolute;
  right: auto;
  text-align: center;
  width: 100%;
}

.show-loading .inline-gallery-loading-message {
  display: block;
}

.photo-count-wrapper {
  align-items: center;
  display: flex;
  filter: drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.4));
  flex-direction: row;
  flex-shrink: 0;
}

.photo-count {
  font-size: var(--body);
  color: white;
}

.gallery-footer {
  align-items: center;
  background: url('~/assets/images/background-fade.png') repeat-x bottom left;
  bottom: 0;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  left: 0;
  padding: 0.3em 1.4em 0.3em 1.2em;
  pointer-events: none;
  position: absolute;
  right: 0;
  transition: background 0.5s ease-in;
}

.search-inline-gallery:not(.desktop)::-webkit-scrollbar {
  display: none;
}

.gallery-footer {
  transition: opacity 0.3s ease-in;
  opacity: 1;
}

.gallery-scrollbar {
  background: rgba(255, 255, 255, 0.35);
  border-radius: 0.3em;
  height: 0.4em;
  margin: 0 0 0 1em;
  pointer-events: none;
  position: relative;
  width: 100%;
}

.scrollbar-pip {
  background: white;
  border-radius: 0.3em;
  height: 0.4em;
  left: 0;
  min-width: 1em;
  position: absolute;
  top: 0;
  z-index: 1;
}

.gallery-in-use .gallery-footer {
  opacity: 0;
}
</style>
